import { useCallback } from "react";
import usePostMessage from "src/pages/hooks/post-message";
import * as xlsx from "xlsx";

type ExcelFileName = string | (() => string);
type ExcelCellValue = string | number | boolean | Date | null | undefined;

export type ExcelColumn<Row> = {
  header: string;
  value: (row: Row) => ExcelCellValue;
};

type CreateExcelWorkbookOptions<Row> = {
  columns: ExcelColumn<Row>[];
  rows: Row[];
  sheetName: string;
};

type DownloadExcelWorkbookOptions = {
  addFileNameSuffix?: boolean;
  fileName: ExcelFileName;
  webViewFileName?: string;
  workbook: xlsx.WorkBook;
};

type DownloadExcelOptions<Row> = CreateExcelWorkbookOptions<Row> & Omit<DownloadExcelWorkbookOptions, "workbook">;

const resolveFileName = (fileName: ExcelFileName) => (typeof fileName === "function" ? fileName() : fileName);

/** ZIP 엔트리를 DEFLATE 로 저장한다. 웹뷰는 base64 결과물 크기가 그대로 메모리 사용량이 된다. */
const XLSX_WRITE_OPTIONS: xlsx.WritingOptions = { compression: true };

export const createExcelWorkbook = <Row>(options: CreateExcelWorkbookOptions<Row>) => {
  const { columns, rows, sheetName } = options;
  const workbook = xlsx.utils.book_new();
  const sheetRows = [columns.map((column) => column.header), ...rows.map((row) => columns.map((column) => column.value(row) ?? ""))];
  const worksheet = xlsx.utils.aoa_to_sheet(sheetRows);

  xlsx.utils.book_append_sheet(workbook, worksheet, sheetName);

  return workbook;
};

const useExcelDownload = () => {
  const { postMessageBase64FileDownload } = usePostMessage();

  const downloadExcelWorkbook = useCallback(
    (options: DownloadExcelWorkbookOptions) => {
      const fileName = resolveFileName(options.fileName);

      if (window.ReactNativeWebView) {
        const base64Text = xlsx.write(options.workbook, { type: "base64", bookType: "xlsx", ...XLSX_WRITE_OPTIONS });
        postMessageBase64FileDownload(base64Text, options.webViewFileName || fileName, { addFileNameSuffix: options.addFileNameSuffix });
        return;
      }

      xlsx.writeFile(options.workbook, fileName, XLSX_WRITE_OPTIONS);
    },
    [postMessageBase64FileDownload],
  );

  const downloadExcel = useCallback(
    <Row>(options: DownloadExcelOptions<Row>) => {
      const workbook = createExcelWorkbook(options);
      downloadExcelWorkbook({
        addFileNameSuffix: options.addFileNameSuffix,
        fileName: options.fileName,
        webViewFileName: options.webViewFileName,
        workbook,
      });
    },
    [downloadExcelWorkbook],
  );

  return {
    createExcelWorkbook,
    downloadExcel,
    downloadExcelWorkbook,
  };
};

export default useExcelDownload;
